import { z } from 'zod';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { ApiError } from './api';

/**
 * Cliente HTTP + hooks para la biblioteca de PDFs y búsqueda RAG (M2.3).
 *
 * Endpoints cubiertos:
 *   GET    /api/knowledge/documents
 *   POST   /api/knowledge/upload        (multipart/form-data, campo `file`)
 *   DELETE /api/knowledge/documents/:id
 *   POST   /api/knowledge/search        ({ query, topK? })
 *
 * Errores específicos (mapeados a clases para que la UI haga toasts adecuados):
 *   409 already_uploaded  → KnowledgeAlreadyUploadedError
 *   413 too_large         → KnowledgeFileTooLargeError
 *   422 pdf_sin_texto     → KnowledgePdfWithoutTextError
 *   503 voyage_*          → VoyageNotConfiguredError (re-exportado de voyage.ts)
 */

const API_BASE_URL: string = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

import { VoyageNotConfiguredError } from './voyage';

// ---------------------------------------------------------------------------
// Errores específicos
// ---------------------------------------------------------------------------

/** 409: ya existe un documento con el mismo content_hash para este profile. */
export class KnowledgeAlreadyUploadedError extends ApiError {
  public readonly existingDocumentId?: number;
  constructor(existingDocumentId?: number) {
    super('Este PDF ya estaba subido', 409);
    this.name = 'KnowledgeAlreadyUploadedError';
    if (typeof existingDocumentId === 'number') {
      this.existingDocumentId = existingDocumentId;
    }
  }
}

/** 413: el PDF supera el tamaño máximo permitido (30 MB). */
export class KnowledgeFileTooLargeError extends ApiError {
  constructor() {
    super('PDF demasiado grande (máximo 30 MB)', 413);
    this.name = 'KnowledgeFileTooLargeError';
  }
}

/** 422: el PDF no contiene texto extraíble (probablemente sólo imágenes). */
export class KnowledgePdfWithoutTextError extends ApiError {
  constructor() {
    super('El PDF no contiene texto extraíble', 422);
    this.name = 'KnowledgePdfWithoutTextError';
  }
}

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

export const DocumentSchema = z.object({
  id: z.number().int().positive(),
  title: z.string().min(1),
  originalFilename: z.string().min(1),
  contentHash: z.string().min(1),
  totalPages: z.number().int().nonnegative().nullable().optional(),
  totalChunks: z.number().int().nonnegative(),
  bytesSize: z.number().int().nonnegative(),
  uploadedAt: z.string(),
});
export type KnowledgeDocument = z.infer<typeof DocumentSchema>;

export const DocumentsResponseSchema = z.object({
  documents: z.array(DocumentSchema),
});
export type DocumentsResponse = z.infer<typeof DocumentsResponseSchema>;

export const UploadResponseSchema = z.object({
  document: DocumentSchema,
  chunks: z.number().int().nonnegative(),
  embedTokensUsed: z.number().int().nonnegative().optional(),
});
export type UploadResponse = z.infer<typeof UploadResponseSchema>;

export const SearchResultSchema = z.object({
  chunkId: z.number().int().positive(),
  documentId: z.number().int().positive(),
  documentTitle: z.string(),
  idx: z.number().int().nonnegative(),
  pageStart: z.number().int().nullable().optional(),
  pageEnd: z.number().int().nullable().optional(),
  text: z.string(),
  score: z.number(),
});
export type SearchResult = z.infer<typeof SearchResultSchema>;

export const SearchResponseSchema = z.object({
  results: z.array(SearchResultSchema),
});
export type SearchResponse = z.infer<typeof SearchResponseSchema>;

// ---------------------------------------------------------------------------
// HTTP helpers
// ---------------------------------------------------------------------------

async function readJsonSafe(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function pickString(body: unknown, key: string): string | undefined {
  if (body && typeof body === 'object' && key in body) {
    const v = (body as Record<string, unknown>)[key];
    if (typeof v === 'string') return v;
  }
  return undefined;
}

function pickNumber(body: unknown, key: string): number | undefined {
  if (body && typeof body === 'object' && key in body) {
    const v = (body as Record<string, unknown>)[key];
    if (typeof v === 'number') return v;
  }
  return undefined;
}

/**
 * Mapea respuestas no-OK a errores tipados. Si el código no es uno de los
 * conocidos, lanza ApiError genérico con el status.
 */
async function throwForNonOk(response: Response): Promise<never> {
  const body = await readJsonSafe(response);
  const errCode = pickString(body, 'error') ?? '';
  const hint = pickString(body, 'hint') ?? '';

  if (response.status === 409 || errCode === 'already_uploaded') {
    throw new KnowledgeAlreadyUploadedError(
      pickNumber(body, 'existingDocumentId'),
    );
  }
  if (response.status === 413 || errCode === 'too_large') {
    throw new KnowledgeFileTooLargeError();
  }
  if (response.status === 422 || errCode === 'pdf_sin_texto') {
    throw new KnowledgePdfWithoutTextError();
  }
  if (
    response.status === 503 &&
    (errCode === 'voyage_no_configurado' ||
      errCode === 'voyage_not_configured')
  ) {
    throw new VoyageNotConfiguredError(hint);
  }
  throw new ApiError(
    `Respuesta no OK del backend: ${response.status}`,
    response.status,
  );
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export async function listDocuments(): Promise<KnowledgeDocument[]> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/knowledge/documents`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (!response.ok) {
    await throwForNonOk(response);
  }
  const json = await readJsonSafe(response);
  // El backend puede devolver `{ documents: [...] }` o `[...]` directamente;
  // toleramos ambos por defensa.
  const arrayParsed = z.array(DocumentSchema).safeParse(json);
  if (arrayParsed.success) return arrayParsed.data;
  const wrappedParsed = DocumentsResponseSchema.safeParse(json);
  if (wrappedParsed.success) return wrappedParsed.data.documents;
  throw new ApiError(
    'Respuesta /api/knowledge/documents no cumple esquema',
    response.status,
    wrappedParsed.error,
  );
}

/**
 * Sube un PDF. Devuelve metadata del documento creado + nº de chunks.
 *
 * Notar que NO usamos `Content-Type: application/json` aquí — `FormData` hace
 * que el navegador ponga `multipart/form-data; boundary=...` automáticamente.
 */
export async function uploadPdf(file: File): Promise<UploadResponse> {
  const form = new FormData();
  form.append('file', file, file.name);

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/knowledge/upload`, {
      method: 'POST',
      credentials: 'include',
      body: form,
      // NO añadir Content-Type — el browser pondrá el boundary correcto.
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (!response.ok) {
    await throwForNonOk(response);
  }
  const json = await readJsonSafe(response);
  const parsed = UploadResponseSchema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError(
      'Respuesta /api/knowledge/upload no cumple esquema',
      response.status,
      parsed.error,
    );
  }
  return parsed.data;
}

export async function deleteDocument(id: number): Promise<void> {
  let response: Response;
  try {
    response = await fetch(
      `${API_BASE_URL}/api/knowledge/documents/${encodeURIComponent(String(id))}`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      },
    );
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (response.status === 204 || response.ok) return;
  await throwForNonOk(response);
}

export async function searchKnowledge(
  query: string,
  topK = 8,
): Promise<SearchResult[]> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/api/knowledge/search`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ query, topK }),
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (!response.ok) {
    await throwForNonOk(response);
  }
  const json = await readJsonSafe(response);
  const parsed = SearchResponseSchema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError(
      'Respuesta /api/knowledge/search no cumple esquema',
      response.status,
      parsed.error,
    );
  }
  return parsed.data.results;
}

// ---------------------------------------------------------------------------
// Hooks (TanStack Query)
// ---------------------------------------------------------------------------

export const KNOWLEDGE_DOCUMENTS_QUERY_KEY = ['knowledge', 'documents'] as const;
export const KNOWLEDGE_SEARCH_QUERY_KEY = ['knowledge', 'search'] as const;

export function useDocuments(): UseQueryResult<KnowledgeDocument[], Error> {
  return useQuery<KnowledgeDocument[], Error>({
    queryKey: KNOWLEDGE_DOCUMENTS_QUERY_KEY,
    queryFn: listDocuments,
    staleTime: 0,
    retry: 1,
  });
}

export function useUploadPdf(): UseMutationResult<UploadResponse, Error, File> {
  const qc = useQueryClient();
  return useMutation<UploadResponse, Error, File>({
    mutationFn: (file) => uploadPdf(file),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KNOWLEDGE_DOCUMENTS_QUERY_KEY });
    },
  });
}

export function useDeleteDocument(): UseMutationResult<void, Error, number> {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) => deleteDocument(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KNOWLEDGE_DOCUMENTS_QUERY_KEY });
    },
  });
}

/**
 * Hook lazy: el caller controla cuándo ejecutar la búsqueda con `enabled` y
 * actualizando `query`. Mantenemos las queries por key=[query, topK] para que
 * TanStack haga cache de las búsquedas repetidas.
 */
export function useSearchKnowledge(
  query: string,
  topK = 8,
  options: { enabled?: boolean } = {},
): UseQueryResult<SearchResult[], Error> {
  const { enabled = false } = options;
  return useQuery<SearchResult[], Error>({
    queryKey: [...KNOWLEDGE_SEARCH_QUERY_KEY, query, topK],
    queryFn: () => searchKnowledge(query, topK),
    enabled: enabled && query.trim().length > 0,
    staleTime: 60_000,
    retry: 1,
  });
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convierte bytes a una cadena legible (KB, MB). */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
